import axios from "axios";
import { API_URL } from "@/config";
import { useSubscribeForFile } from "@/utils/useSubscribeForFile";

const store = {
  namespaced: true,

  actions: {
    async getMany(_, criteria) {
      const res = await axios.get(`${API_URL}/parents`, { params: criteria });

      if (!res.data || typeof res.data?.parents?.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getOne(_, parentId) {
      const res = await axios.get(`${API_URL}/parents/${parentId}`);

      if (!res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async create(_, parentData) {
      const res = await axios.post(`${API_URL}/parents`, parentData);

      if (res.status !== 201 || !res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async update(_, { parentId, parentData }) {
      const res = await axios.put(`${API_URL}/parents/${parentId}`, parentData);

      if (res.status !== 200 || !res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async remove(_, parentId) {
      const res = await axios.delete(`${API_URL}/parents/${parentId}`);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async paymentsOverdue(_, parentId) {
      const res = await axios.post(`${API_URL}/parents/${parentId}/paymentsOverdue`);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async activate(_, parentId) {
      const res = await axios.post(`${API_URL}/parents/${parentId}/activate`);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async deactivate(_, parentId) {
      const res = await axios.post(`${API_URL}/parents/${parentId}/deactivate`);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },
    async archive(_, parentId) {
      const res = await axios.post(`${API_URL}/parents/${parentId}/archive`);

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async unarchive(_, parentId) {
      const res = await axios.post(`${API_URL}/parents/${parentId}/unarchive`);

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async getChildren(_, { filters = {}, parentId, pageNumber = 1 }) {
      const q = {
        ...filters,
        page: pageNumber,
      };

      const res = await axios.get(`${API_URL}/parents/${parentId}/children`, { params: q });

      if (!res.data || typeof res.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getChild(
      _,
      {
        parentId,
        childId,
        withAutomarkedWeekdays,
        withAutoselectedMealTypes,
        withAutoselectedMealMenusSets,
      },
    ) {
      const params = {
        withAutomarkedWeekdays: withAutomarkedWeekdays ? 1 : 0,
        withAutoselectedMealTypes: withAutoselectedMealTypes ? 1 : 0,
        withAutoselectedMealMenusSets: withAutoselectedMealMenusSets ? 1 : 0,
      };

      const res = await axios.get(`${API_URL}/parents/${parentId}/children/${childId}`, {
        params,
      });

      if (res.status !== 200 || !res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async createChild(_, { parentId, childData }) {
      const res = await axios.post(`${API_URL}/parents/${parentId}/children`, childData);

      if (res.status !== 201 || !res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async updateChild(_, { parentId, childId, childData }) {
      const res = await axios.put(`${API_URL}/parents/${parentId}/children/${childId}`, childData);

      if (res.status !== 200 || !res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async removeChild(_, { parentId, childId }) {
      const res = await axios.delete(`${API_URL}/parents/${parentId}/children/${childId}`);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async export({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/parentsReport", {
        url: `${API_URL}/reports/parentsReport`,
        method: "POST",
        data: criteria,
      });
    },

    async downloadParentsListPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadParentsListPdf", {
        url: `${API_URL}/reports/exportParents/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });
    },

    async printParentsListPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printParentsListPdf",
        {
          url: `${API_URL}/reports/exportParents/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },
  },
};

export default store;
